import { unionBy } from 'lodash-es';
import { DateTime } from 'luxon';
import { deepCheckValid, deepCompareEQ } from '@cf-shared/deep-helpers';
import { getEntryLocalTimeInDesiredTimeZone, LogRecordState, LogRecordType, Log_Entry } from '@cf-shared/data-model/user-log';
import { Log_Meal } from '@cf-shared/data-model/log-meal';

/**
 * Which entries a backlog rebuild writes to the database.
 *
 * `generateBacklogInMemoryAsync` rebuilds a range of days in memory, then saves
 * only some of what it built. This is that choice. It is a module of its own,
 * free of Angular and of the database, so that it can be unit tested — the same
 * reason `user-db.delete-boundary.ts` sits beside the service.
 */

/** What the rebuild holds when it decides what to save. */
export interface BacklogSaveInput {
  /** the entries read from disk for the rebuilt range, as the rebuild left them
   *  (it marks each one `_deleted` and revives only the ones it keeps) */
  storedEntriesRange: Log_Entry[];
  /** untouched copies of `storedEntriesRange`, taken before the rebuild changed anything */
  storedEntriesRangeUnmodified: Log_Entry[];
  /** everything the rebuild kept for the range */
  scheduledLogEntries: Log_Entry[];
  /** the start of the user's real today */
  todayBoD: DateTime;
  /** ids of the meals the rebuild left off every slot (a day holding more meals than slots).
   *  When the rebuild renamed one of these, its day's new names are not saved. Leave out when
   *  there are none. */
  mealIdsLeftOffSlots?: ReadonlySet<string>;
  /** true while this session's first sync of the user database has not succeeded. A past day's
   *  new entries then come from a schedule the sync has not confirmed, so they are kept in memory
   *  and not saved (#6280). Leave out once the sync has succeeded. */
  holdNewPastEntries?: boolean;
}

/**
 * Did the rebuild change the goal this stored entry carries? Only a day start and a meal carry
 * one: `targets` on both, and the bulk/cut flag `cutMeal` on a meal.
 *
 * Compared with `deepCompareEQ`, which reads a missing field, `undefined` and `false` alike. The
 * plan leaves some goal fields `undefined`, and those vanish once the document is on disk, so a
 * stricter comparison would call every rebuild a change and write the day again each time.
 *
 * A rebuilt goal holding a number that is not finite is never a change worth saving. It comes
 * from a half-built schedule (a user still in the intake has no meal frequency to solve with),
 * and disk would store its NaN as null, so it would compare as changed on every rebuild.
 */
function goalChanged(unmodified: Log_Entry, entry: Log_Entry): boolean {
  const before = unmodified as Partial<Log_Meal>;
  const after = entry as Partial<Log_Meal>;
  if (deepCheckValid(after.targets, 'targets')) {
    return false;
  }
  return !deepCompareEQ({ targets: before.targets, cutMeal: before.cutMeal }, { targets: after.targets, cutMeal: after.cutMeal });
}

/**
 * Is the name the only thing the rebuild changed on this stored meal? Asked on a past day, where
 * the rebuild's other changes (a meal moved onto its slot time, a goal filled in) are never saved
 * on purpose.
 *
 * `planningTargets` is left out of the comparison. It is not the rebuild's change: the day-total
 * pass recomputes it on every load, so it differs on most meals and says nothing about them.
 */
function onlyNameChanged(unmodified: Log_Meal, meal: Log_Meal): boolean {
  const withoutName = (m: Log_Meal) => {
    const { name: _name, planningTargets: _planningTargets, _deleted, ...rest } = m;
    return rest;
  };
  return deepCompareEQ(withoutName(unmodified), withoutName(meal));
}

/** The stored meals saved for a change the rebuild made to them, by kind of change. */
interface MealIdsToSave {
  /** meals whose new name is saved (#6208) */
  renamed: Set<string>;
  /** meals on today or a later day whose new time is saved (df-feac372a8284) */
  moved: Set<string>;
}

/**
 * The stored meals saved for a change the rebuild made to their name or their time.
 *
 * NAMES (#6208). The rebuild names every meal after the slot it sits on ("Meal <slot number>"), so
 * a meal's name changes whenever the day's slot order does.
 *
 * TIMES (df-feac372a8284). On today or a later day every stored meal the rebuild moved onto its
 * slot is saved, so the day's stored times agree with each other. Past days are left as stored.
 * Why, and why the device's time zone does not matter: design-docs/sync-integrity.md, "The
 * rebuild's meal times reach disk too".
 *
 * Both are decided a whole DAY at a time: either every renamed meal of a day is saved or none is.
 * Saving some of a day's new names is exactly how two stored meals come to share one. When a day's
 * names are held back, none of its moves are saved either, because saving a moved meal writes its
 * new name too; the day then stays exactly as stored. A day's names are not saved when:
 *  - a meal the rebuild left off every slot was renamed, or is stored under a name one of the
 *    day's renamed meals had or takes. Two schedule-built meals under one name can be two copies
 *    of ONE meal (a client's copy and a coach's), which must keep counting once (#6207), so such
 *    a day is left as it is stored. A meal left off the slots under a name of its own, such as a
 *    snack added by hand, does not hold the day back;
 *  - it is a past day and the rebuild changed more than the name on one of the renamed meals. The
 *    rebuild's time and goal changes are never saved on a past day, on purpose.
 *
 * On today or a later day a renamed meal is saved as the rebuild left it, which includes a move
 * onto its slot time, the same as the goal-changed save has always written it.
 */
function mealIdsToSaveForRebuildChanges(input: BacklogSaveInput): MealIdsToSave {
  const { scheduledLogEntries, storedEntriesRangeUnmodified, todayBoD } = input;

  const mealsByDay = new Map<string, Log_Meal[]>();
  for (const entry of scheduledLogEntries) {
    if (entry.type !== LogRecordType.Meal) continue;
    const day = getEntryLocalTimeInDesiredTimeZone(entry, todayBoD).toISODate();
    mealsByDay.set(day, [...(mealsByDay.get(day) ?? []), entry as Log_Meal]);
  }

  const ids: MealIdsToSave = { renamed: new Set<string>(), moved: new Set<string>() };
  for (const [day, meals] of mealsByDay) {
    const isPastDay = day < todayBoD.toISODate();
    const renamed = renamedMealsToSaveForDay(meals, isPastDay, input);
    // if the day's names are held back, the day stays as stored: no name and no move is saved
    if (renamed === undefined) continue;
    renamed.forEach(meal => ids.renamed.add(meal._id));
    // a past day's times are never saved
    if (isPastDay) continue;
    meals.filter(meal => timeChanged(storedEntriesRangeUnmodified, meal)).forEach(meal => ids.moved.add(meal._id));
  }
  return ids;
}

/**
 * Did the rebuild move this stored meal? It writes the two time fields only when it moves a meal
 * (`setEntryLocalTime`), so a meal it left alone keeps both exactly as stored. A meal not stored
 * yet has no stored time to differ from.
 */
function timeChanged(storedEntriesRangeUnmodified: Log_Entry[], meal: Log_Meal): boolean {
  const unmodified = storedEntriesRangeUnmodified.find(e => e._id === meal._id);
  return !!unmodified && (unmodified.time_utc !== meal.time_utc || unmodified.timeOffset_min !== meal.timeOffset_min);
}

/**
 * One day's renamed stored meals (an empty list when none was renamed), or `undefined` when that
 * day's names may not be saved.
 */
function renamedMealsToSaveForDay(meals: Log_Meal[], isPastDay: boolean, input: BacklogSaveInput): Log_Meal[] | undefined {
  const { storedEntriesRangeUnmodified, mealIdsLeftOffSlots } = input;
  const renamed: Log_Meal[] = [];
  // every name a renamed meal had or was given, and the stored names of the meals left off the slots
  const namesRenamedFromOrTo = new Set<string>();
  const storedNamesLeftOffSlots: string[] = [];
  for (const meal of meals) {
    const unmodified = storedEntriesRangeUnmodified.find(e => e._id === meal._id) as Log_Meal | undefined;
    // a meal that is not stored yet has no stored name to disagree with
    if (!unmodified) continue;
    const isLeftOffSlots = !!mealIdsLeftOffSlots?.has(meal._id);
    if (isLeftOffSlots) {
      storedNamesLeftOffSlots.push(unmodified.name);
    }
    if (unmodified.name === meal.name) continue;
    // if the rebuild renamed a meal it left off every slot, the day's names are not saved
    if (isLeftOffSlots) return undefined;
    // if this is a past day and the rebuild changed more than the name, the day is not saved
    if (isPastDay && !onlyNameChanged(unmodified, meal)) return undefined;
    renamed.push(meal);
    namesRenamedFromOrTo.add(unmodified.name).add(meal.name);
  }
  // if a meal left off the slots is stored under a name a renamed meal had or takes, the two can
  // be copies of one meal, and saving the new name would part them or join two others
  if (storedNamesLeftOffSlots.some(name => namesRenamedFromOrTo.has(name))) return undefined;
  return renamed;
}

/**
 * The kept entries on a past day that are not on disk yet: the backfill for days this device did
 * not open. These are what `holdNewPastEntries` keeps in memory (#6280).
 */
export function newPastEntries(input: BacklogSaveInput): Log_Entry[] {
  const { storedEntriesRange, scheduledLogEntries, todayBoD } = input;
  return scheduledLogEntries.filter(e =>
    getEntryLocalTimeInDesiredTimeZone(e, todayBoD) < todayBoD
    && !storedEntriesRange.some(stored => stored._id === e._id));
}

/**
 * Choose the entries to save:
 *  - a stored entry the rebuild no longer keeps, when it was deleted or changed;
 *  - a kept entry on a past day that is not on disk yet, unless `holdNewPastEntries` is set;
 *  - a kept entry on today or a later day that is `Planned`;
 *  - a kept, stored entry on today or a later day whose goal the rebuild changed;
 *  - a kept, stored meal the rebuild renamed, on any day, when its whole day's new names can be
 *    saved together (`mealIdsToSaveForRebuildChanges` says when they cannot);
 *  - a kept, stored meal on today or a later day that the rebuild moved onto its slot time, unless
 *    its day's new names are held back.
 *
 * The move case is df-feac372a8284; `mealIdsToSaveForRebuildChanges` says why. See
 * design-docs/sync-integrity.md, "The rebuild's meal names must reach disk".
 *
 * The rename case is #6208. A new name used to stay in memory only, and each meal reached disk
 * under it whenever something else happened to save that meal. Disk then held some of a day's
 * meals under their new names and the rest under their old ones, so two stored meals could share
 * a name, and the day list and total show one meal per name (`pickOneMealPerSlot`). Past days
 * are included so a day already stored that way is put right the next time it is rebuilt. See
 * design-docs/sync-integrity.md, "The rebuild's meal names must reach disk".
 *
 * The goal case is #3976. The rebuild gives today's day start and meals the current plan's
 * goals, and those used to stay in memory only: each record reached disk whenever something
 * else happened to save it, so after a re-plan the saved day goal and the saved meal goals no
 * longer added up. Saving them here puts a day start and its meals into the same batch.
 */
export function chooseBacklogEntriesToSave(input: BacklogSaveInput): Log_Entry[] {
  const { storedEntriesRange, storedEntriesRangeUnmodified, scheduledLogEntries, todayBoD } = input;
  const mealIdsToSave = mealIdsToSaveForRebuildChanges(input);

  // find which entries that are in the database but not in the new schedule or if they were modified but still in the schedule
  const droppedOrChanged = storedEntriesRange.filter(e => {
    const unmodifiedEntry = storedEntriesRangeUnmodified.find(e2 => e2._id === e._id);
    const scheduledEntry = scheduledLogEntries.find(e2 => e2._id === e._id);
    if (scheduledEntry && !scheduledEntry._deleted) {
      return false;
    }
    return e._deleted || (unmodifiedEntry && !deepCompareEQ(unmodifiedEntry, e));
  });

  return unionBy(droppedOrChanged, scheduledLogEntries.filter(e => {
    const entryTime = getEntryLocalTimeInDesiredTimeZone(e, todayBoD);
    if (entryTime < todayBoD) {
      const storedEntry = storedEntriesRange.find(e2 => e2._id === e._id);
      // a past day's new entry is saved only once the sync has confirmed the schedule (#6280)
      if (!storedEntry) {
        return !input.holdNewPastEntries;
      }
      // a stored past entry is saved only for a new meal name
      return mealIdsToSave.renamed.has(e._id);
    } else if (e.state === LogRecordState.Planned) {
      // This should cover meal that are planned in the future
      return true;
    } else {
      const unmodifiedEntry = storedEntriesRangeUnmodified.find(e2 => e2._id === e._id);
      return unmodifiedEntry !== undefined
        && (goalChanged(unmodifiedEntry, e) || mealIdsToSave.renamed.has(e._id) || mealIdsToSave.moved.has(e._id));
    }
  }), e => e._id);
}
